/**
 * Character Classes
 * Specialized player classes with unique abilities
 */

import { Player } from './Player.js';
import { THREE } from "../config/ThreeConfig.js";
import { getAbilityGlbUrl } from "../assets/AbilityArt.js";

function markAbilityObjectReadable(root) {
  if (!root) return root;
  root.frustumCulled = false;
  root.renderOrder = 22;
  root.traverse?.((obj) => {
    if (!obj) return;
    obj.frustumCulled = false;
    obj.renderOrder = 22;
    if (!obj.isMesh) return;
    obj.castShadow = false;
    obj.receiveShadow = false;
    const mat = obj.material;
    if (mat && typeof mat === "object") {
      if ("fog" in mat) mat.fog = false;
      if ("toneMapped" in mat) mat.toneMapped = false;
      if ("side" in mat) mat.side = THREE.DoubleSide;
      if ("emissiveIntensity" in mat) mat.emissiveIntensity = Math.max(Number(mat.emissiveIntensity) || 0, 0.55);
      if ("emissive" in mat && mat.emissive?.setHex) {
        if ((Number(mat.emissiveIntensity) || 0) < 0.25) mat.emissiveIntensity = 0.25;
      }
      mat.needsUpdate = true;
    }
  });
  return root;
}

function createFallbackDroneVisual({ color = 0x99e8ff, scale = 1 } = {}) {
  const g = new THREE.Group();
  const core = new THREE.Mesh(
    new THREE.OctahedronGeometry(1.45, 0),
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.92,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    })
  );
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(2.3, 0.24, 8, 18),
    new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.5,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    })
  );
  ring.rotation.x = Math.PI / 2;
  g.add(core);
  g.add(ring);
  g.scale.setScalar(Math.max(0.01, Number(scale) || 1));
  g.userData.isAbilityFallback = true;
  markAbilityObjectReadable(g);
  return g;
}

function createFallbackShockwaveVisual({ color = 0xff8800 } = {}) {
  const g = new THREE.Group();
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(14, 1.4, 14, 48),
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    })
  );
  ring.rotation.x = Math.PI / 2;
  const core = new THREE.Mesh(
    new THREE.SphereGeometry(4, 12, 12),
    new THREE.MeshBasicMaterial({
      color: 0xffcc00,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    })
  );
  g.add(ring);
  g.add(core);
  g.userData.isAbilityFallback = true;
  markAbilityObjectReadable(g);
  return g;
}

function createFallbackChargeCoreVisual({ color = 0x88d6ff } = {}) {
  const mesh = new THREE.Mesh(
    new THREE.IcosahedronGeometry(5.2, 1),
    new THREE.MeshBasicMaterial({
      color,
      wireframe: true,
      transparent: true,
      opacity: 0.82,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    })
  );
  mesh.userData.isAbilityFallback = true;
  markAbilityObjectReadable(mesh);
  return mesh;
}

function createFallbackCloakShellVisual({ color = 0xff66ff } = {}) {
  const mesh = new THREE.Mesh(
    new THREE.SphereGeometry(16, 18, 14),
    new THREE.MeshBasicMaterial({
      color,
      wireframe: true,
      transparent: true,
      opacity: 0.22,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    })
  );
  mesh.userData.isAbilityFallback = true;
  markAbilityObjectReadable(mesh);
  return mesh;
}

/**
 * Honey Badger - RAGEBURROW Mk IV
 * High damage, no brakes, aggressive
 */
export class HoneyBadger extends Player {
  constructor(game, options = {}) {
    super(game, { ...options, character: 'honeybadger' });
    
    // Unique stats
    this.speed = 70;
    this.maxSpeed = 120;
    this.damage = 15;
    this.health = 80;
    this.shields = 80;
    this.maxHealth = this.health;
    this.maxShields = this.shields;
    
    // Personality Matrix
    this.personality = {
      aggression: 95,
      equity: 10,
      stability: 20,
      chaos: 90
    };
    
    // Ability
    this.canRam = true;
    this.ramDamage = 50;
    this.shieldMesh = null;

    // Special ability (cooldown-gated): Rage Ram.
    // Activated by `E` (preferred) or by holding Boost+Forward (legacy feel).
    this.specialName = "UNHINGED VECTOR";
    this.specialButtonHint = "E";
    this.specialCooldownMs = 8200;
    this.specialDurationMs = 2200;
    this.specialSummary = "Short invulnerable ram window for smashing through close threats.";
    this.specialUsage = "Press E when READY, then steer into a nearby enemy before the ram window ends.";
    this.specialEffect = "Impact deals heavy ram damage and grants brief invulnerability during the charge.";
    this.specialTacticalTip = "Best used at point-blank range or to punch through a crowded lane.";
    this._ramActiveUntilMs = 0;
    this._ramShockwave = null;
    this._ramShockwavePromise = null;
  }

  loadRamShockwave() {
    if (this._ramShockwave) return Promise.resolve(this._ramShockwave);
    const fallback = createFallbackShockwaveVisual({ color: 0xff8800 });
    fallback.name = "honeybadger-ram-shockwave-fallback";
    fallback.visible = false;
    fallback.position.set(0, 4, -18);
    this.object3D.add(fallback);
    this._ramShockwave = fallback;
    return Promise.resolve(fallback);
  }

  ensureShieldMesh() {
    if (this.shieldMesh) return;

    // Burnt Orange Shields Visual
    const shieldGeometry = new THREE.SphereGeometry(15, 16, 16);
    const shieldMaterial = new THREE.MeshPhongMaterial({
      color: 0xff6600,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide,
      emissive: 0xff3300,
      emissiveIntensity: 0.2,
    });
    this.shieldMesh = new THREE.Mesh(shieldGeometry, shieldMaterial);
    this.shieldMesh.visible = true;
    this.object3D.add(this.shieldMesh);
  }

  preloadAbilityAssets() {
    this.ensureShieldMesh();
    return this.loadRamShockwave();
  }
  
  async createShip() {
    // Use the base ship pipeline: instant placeholder + async GLB swap.
    // This avoids blocking gameplay while large GLBs load.
    super.createShip();
  }
  
  update(deltaTime, input) {
    super.update(deltaTime, input);
    
    // Update shield visibility
    if (this.shieldMesh) {
      this.shieldMesh.visible = this.shields > 0;
      if (this.shields > 0) {
        const rot = this.shieldMesh.rotation;
        if (rot && typeof rot === "object") {
          if (typeof rot.y === "number") rot.y += deltaTime * 0.5;
          if (typeof rot.x === "number") rot.x += deltaTime * 0.2;
        }
      }
    }

    // No brakes - Honey Badger doesn't do brakes
    if (input.brake) {
      input.brake = false;
    }
    
    // CONTINUOUS ACCELERATION: RAGEBURROW keeps building momentum
    // If moving forward, increase speed up to maxSpeed
    if (input.forward) {
      this.speed = Math.min(this.maxSpeed, this.speed + 5 * deltaTime);
    } else {
      // Slowly decelerate back to base speed (70) when not accelerating
      this.speed = Math.max(70, this.speed - 2 * deltaTime);
    }
    
    const nowMs = this.getNowMs();

    const isRamming = nowMs < this._ramActiveUntilMs;
    this.showActivatedShip(isRamming);
    if (isRamming) this.loadRamShockwave();
    if (this._ramShockwave) {
      this._ramShockwave.visible = isRamming;
      if (isRamming) {
        const remaining = Math.max(0, this._ramActiveUntilMs - nowMs);
        const t = Math.max(0, Math.min(1, remaining / Math.max(1, Number(this.specialDurationMs) || 1200)));
        const pulse = 0.9 + 0.2 * Math.sin((nowMs / 1000) * 9.5);
        const scale = pulse * (1.05 + (1 - t) * 0.35);
        this._ramShockwave.scale.setScalar(scale);
        this._ramShockwave.rotation.z += deltaTime * 2.2;
      }
    }
    if (isRamming) {
      // Check for collision with enemies
      for (const enemy of this.game.enemies || []) {
        if (!enemy?.object3D || enemy.isDestroyed) continue;
        const distance = this.object3D.position.distanceTo(enemy.object3D.position);
        if (distance < 15) {
          enemy.takeDamage(this.ramDamage);
          this.takeDamage(10);

          // Portrait reaction (best effort)
          this.game?.uiManager?.setCharacterExpression?.(this.character, "smug", { ttlMs: 950 });
          
          // Trigger "Peak" dialogue on successful ram
          if (this.game.dialogueManager) {
            this.game.dialogueManager.trigger('honeybadger', 'ram_success');
          }
        }
      }
    }
  }

  activateSpecial(nowMs) {
    if (this._ramActiveUntilMs && nowMs < this._ramActiveUntilMs) return false;
    this._ramActiveUntilMs = nowMs + Math.max(250, Number(this.specialDurationMs) || 1200);
    // Small invuln window so the move feels heroic.
    this.isInvulnerable = true;
    this.invulnerableTime = Math.max(this.invulnerableTime, 0.6);
    this.game?.uiManager?.setCharacterExpression?.(this.character, "angry", { ttlMs: 800 });
    this.loadRamShockwave();
    return true;
  }
  
  getQuote() {
    const quotes = [
      "Why are brakes even legal?",
      "I'll land on that asteroid. Backwards.",
      "The explosion was a feature, not a bug.",
      "Autopilot? More like auto-pilot-error.",
    ];
    return quotes[Math.floor(Math.random() * quotes.length)];
  }
}

/**
 * Octopus - MULTIVEIL
 * Multiple drones, strategic, adaptive
 */
export class Octopus extends Player {
  constructor(game, options = {}) {
    super(game, { ...options, character: 'octopus' });
    
    // Unique stats
    this.speed = 50;
    this.maxSpeed = 90;
    this.damage = 10;
    this.health = 110;
    this.shields = 110;
    this.maxHealth = this.health;
    this.maxShields = this.shields;

    // Personality Matrix
    this.personality = {
      aggression: 30,
      equity: 90,
      stability: 80,
      chaos: 10
    };
    
    // Drones
    this.drones = [];
    this.maxDrones = 8;
    this.droneRange = 100;
    this._droneRegenAcc = 0;
    this._droneFireAcc = 0;
    this._droneFireIndex = 0;

    // Special ability: Drone Salvo (burst fire window).
    this.specialName = "DISTRIBUTED PRESENCE";
    this.specialButtonHint = "E";
    this.specialCooldownMs = 12000;
    this.specialDurationMs = 3200;
    this.specialSummary = "Deploys the full drone board and turns it into a burst-fire salvo.";
    this.specialUsage = "Press E before a wave or boss cluster; drones auto-target the nearest enemy.";
    this.specialEffect = "Eight drones orbit the ship and fire rapid support shots during the active window.";
    this.specialTacticalTip = "Keep enemies near the reticle corridor so the drones have clean firing lanes.";
    this._salvoUntilMs = 0;
    this._salvoStartAtMs = 0;
    this._droneTemplate = null;
    this._droneTemplatePromise = null;
  }

  loadDroneTemplate() {
    if (this._droneTemplatePromise) return this._droneTemplatePromise;
    const url = getAbilityGlbUrl("multiveil_drone");
    if (!url) return Promise.resolve(null);

    // Use a larger target size so drones read clearly in motion.
    this._droneTemplatePromise = this.createGlbInstance(url, { targetMaxDim: 10 }).then((inst) => {
      if (!inst) {
        this._droneTemplatePromise = null;
        return null;
      }
      // Make ability GLBs punchy/readable (without mutating shared materials).
      markAbilityObjectReadable(inst);
      inst.userData.isAbilityGlb = true;
      inst.traverse((obj) => {
        if (!obj?.isMesh) return;
        const mat = obj.material;
        if (mat && typeof mat === "object") {
          if ("fog" in mat) mat.fog = false;
          if ("toneMapped" in mat) mat.toneMapped = false;
          if ("emissiveIntensity" in mat) mat.emissiveIntensity = Math.max(Number(mat.emissiveIntensity) || 0, 0.65);
          mat.needsUpdate = true;
        }
      });
      this._droneTemplate = inst;
      // Upgrade existing procedural drones to the GLB look, best effort.
      for (const d of this.drones) {
        if (!d?.mesh) continue;
        if (d.mesh?.userData?.isAbilityGlb) continue;
        try {
          this.object3D.remove(d.mesh);
        } catch {
          // ignore
        }
        const m = this._droneTemplate.clone(true);
        m.name = "octopus-drone";
        m.userData.isAbilityGlb = true;
        m.visible = true;
        markAbilityObjectReadable(m);
        this.object3D.add(m);
        d.mesh = m;
      }
      return inst;
    }).catch(() => {
      this._droneTemplatePromise = null;
      return null;
    });

    return this._droneTemplatePromise;
  }

  addDrone() {
    const mesh = (() => {
      if (this._droneTemplate) {
        const m = this._droneTemplate.clone(true);
        m.userData.isAbilityGlb = true;
        m.visible = true;
        markAbilityObjectReadable(m);
        return m;
      }

      const pending = createFallbackDroneVisual({ color: 0x9fe9ff, scale: 0.95 });
      pending.name = "octopus-drone-fallback";
      pending.visible = true;
      pending.userData.pendingAbilityGlb = true;
      return pending;
    })();
    mesh.name = "octopus-drone";
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.frustumCulled = false;
    this.object3D.add(mesh);

    const i = this.drones.length;
    const angle = (i / Math.max(1, this.maxDrones)) * Math.PI * 2;
    this.drones.push({
      mesh,
      angle,
      radius: 14,
      speed: 1.3 + i * 0.08,
    });
  }

  ensureDrones() {
    if (this._dronesReady) return;
    this._dronesReady = true;
    const initial = Math.min(4, this.maxDrones);
    for (let i = 0; i < initial; i++) this.addDrone();
  }

  preloadAbilityAssets() {
    // Drones are core fantasy; ensure they're present immediately and start the GLB upgrade.
    this.ensureDrones();
    return this.loadDroneTemplate();
  }
  
  async createShip() {
    // Use the base ship pipeline: instant placeholder + async GLB swap.
    super.createShip();
    // Drones should appear immediately (never wait for ship GLB).
    this.ensureDrones();
    this.loadDroneTemplate();
  }
  
  update(deltaTime, input) {
    super.update(deltaTime, input);

    this.ensureDrones();
    const nowMs = this.getNowMs();
    const isSalvo = nowMs < this._salvoUntilMs;
    this.showActivatedShip(isSalvo);
    const salvoT =
      isSalvo && this._salvoStartAtMs
        ? Math.max(0, Math.min(1, (nowMs - this._salvoStartAtMs) / 220))
        : 0;

    // Deterministic drone regen (no RNG) so the ability is consistent.
    this._droneRegenAcc += Math.max(0, deltaTime);
    if (this.drones.length < this.maxDrones && this._droneRegenAcc >= 1.25) {
      this._droneRegenAcc = 0;
      this.addDrone();
      if (this.game.dialogueManager) this.game.dialogueManager.trigger('octopus', 'drone_deploy');
    }

    // Visual: drones flare + speed up during salvo (make the special *very* obvious).
    const pulse = 1 + (isSalvo ? (0.42 + 0.10 * Math.sin((nowMs / 1000) * 11.5)) : 0);
    for (const d of this.drones) {
      if (!d?.mesh) continue;
      d.mesh.scale.setScalar(pulse);
      // If procedural mesh, brighten emissive.
      const mat = d.mesh?.material;
      if (mat && typeof mat === "object" && "emissiveIntensity" in mat) {
        mat.emissiveIntensity = isSalvo ? 1.35 : 0.55;
        mat.needsUpdate = true;
      }
    }

    // Orbit drones around the ship.
    for (let i = 0; i < this.drones.length; i++) {
      const d = this.drones[i];
      if (!d?.mesh) continue;
      const orbitBoost = isSalvo ? 1.85 : 1.0;
      d.angle += (d.speed || 1.3) * deltaTime * orbitBoost;
      const baseR = d.radius || 14;
      const r = isSalvo ? (baseR + 9 * salvoT) : baseR;
      const x = Math.cos(d.angle) * r;
      const z = Math.sin(d.angle) * 9;
      const y = 2 + Math.sin(d.angle * 2.0) * (isSalvo ? 2.15 : 1.2);
      d.mesh.position.set(x, y, z);
    }

    // Drone auto-fire at nearest enemy in range.
    this._droneFireAcc += Math.max(0, deltaTime);
    const interval = isSalvo ? 0.18 : 0.55;
    if (this._droneFireAcc >= interval) {
      this._droneFireAcc = 0;
      const enemies = this.game?.enemies || [];
      let nearest = null;
      let best = Infinity;
      for (const e of enemies) {
        if (!e?.object3D || e.isDestroyed) continue;
        const dist = e.object3D.position.distanceTo(this.object3D.position);
        if (dist < best) {
          best = dist;
          nearest = e;
        }
      }
      if (nearest && best <= this.droneRange && this.game?.enemyManager?.createProjectile) {
        const shots = isSalvo ? Math.min(4, this.drones.length) : 1;
        for (let s = 0; s < shots; s++) {
          const drone = this.drones[this._droneFireIndex % Math.max(1, this.drones.length)];
          this._droneFireIndex++;
          const origin = drone?.mesh?.getWorldPosition
            ? drone.mesh.getWorldPosition(new THREE.Vector3())
            : this.object3D.position.clone();
          const dir = new THREE.Vector3()
            .subVectors(nearest.object3D.position, origin)
            .normalize();
          const dmg = Math.max(4, Math.round(this.getProjectileDamage() * (isSalvo ? 0.75 : 0.65)));
          const proj = this.game.enemyManager.createProjectile(origin, dir, 260, dmg, true);
          this.game.scene?.add?.(proj.object3D);
          if (Array.isArray(this.game.projectiles)) this.game.projectiles.push(proj);
        }
      }
    }
  }

  activateSpecial(nowMs) {
    this.ensureDrones();
    this.loadDroneTemplate();
    // Make the activation unmissable: instantly deploy to full formation.
    while (this.drones.length < this.maxDrones) this.addDrone();
    this._salvoUntilMs = nowMs + Math.max(300, Number(this.specialDurationMs) || 1600);
    this._salvoStartAtMs = nowMs;
    // Fire on next tick.
    this._droneFireAcc = 999;
    this.game?.uiManager?.setCharacterExpression?.(this.character, "smug", { ttlMs: 900 });
    // Make it visually obvious immediately.
    this.game?.feedback?.shakeScreen?.(0.16, 0.22);
    return true;
  }
  
  getQuote() {
    const quotes = [
      "Interesting. Continue.",
      "Let's explore governance options first.",
      "I've already acquired the company that makes your missiles.",
      "We should discuss equity before the explosion.",
    ];
    return quotes[Math.floor(Math.random() * quotes.length)];
  }
}

/**
 * Elephant - GRAY MATTER
 * Heavy armor, slow, methodical
 */
export class Elephant extends Player {
  constructor(game, options = {}) {
    super(game, { ...options, character: 'elephant' });
    
    // Unique stats
    this.speed = 30;
    this.maxSpeed = 60;
    this.damage = 20;
    this.health = 150;
    this.shields = 150;
    this.maxHealth = this.health;
    this.maxShields = this.shields;

    // Personality Matrix
    this.personality = {
      aggression: 20,
      equity: 70,
      stability: 95,
      chaos: 5
    };
    
    // Ability
    this.measuredCharge = false;
    this.chargeTimer = 0;
    this.chargeMaxTime = 3;

    // Special ability: Charge Shot (one empowered shot, cooldown-gated).
    // Activated by `E` / `Y` (preferred) or by holding Boost+Forward to begin charging.
    this.specialName = "MEASURED CHARGE";
    this.specialButtonHint = "E";
    this.specialCooldownMs = 13000;
    this.specialDurationMs = 0;
    this.specialSummary = "Charges one deliberate high-damage shot.";
    this.specialUsage = "Press E, survive the charge-up, then fire while the core is primed.";
    this.specialEffect = "The next shot jumps to heavy damage after the charge completes.";
    this.specialTacticalTip = "Use it before a boss weak point or a tough corporate heavy enters center lane.";
    this._chargeStartedAtMs = 0;
    this._chargedUntilMs = 0;
    this._chargeCore = null;
    this._chargeCorePromise = null;
  }

  loadChargeCore() {
    if (this._chargeCorePromise) return this._chargeCorePromise;
    const ensureFallback = () => {
      if (this._chargeCore) return this._chargeCore;
      const fallback = createFallbackChargeCoreVisual({ color: 0x88d6ff });
      fallback.name = "elephant-charge-core-fallback";
      fallback.visible = false;
      fallback.position.set(0, 15, -4);
      this.object3D.add(fallback);
      this._chargeCore = fallback;
      return fallback;
    };
    const url = getAbilityGlbUrl("gray_matter_charge_core");
    if (!url) {
      return Promise.resolve(ensureFallback());
    }
    ensureFallback();
    this._chargeCorePromise = this.createGlbInstance(url, { targetMaxDim: 22 }).then((inst) => {
      if (!inst) {
        this._chargeCorePromise = null;
        return ensureFallback();
      }
      inst.name = "elephant-charge-core";
      inst.userData.isAbilityGlb = true;
      inst.visible = false;
      inst.position.set(0, 15, -4);
      markAbilityObjectReadable(inst);
      if (this._chargeCore && this._chargeCore !== inst) {
        try {
          this.object3D.remove(this._chargeCore);
        } catch {
          // ignore
        }
      }
      this.object3D.add(inst);
      this._chargeCore = inst;
      return inst;
    }).catch(() => {
      this._chargeCorePromise = null;
      return ensureFallback();
    });
    return this._chargeCorePromise;
  }

  preloadAbilityAssets() {
    return this.loadChargeCore();
  }
  
  async createShip() {
    // Use the base ship pipeline: instant placeholder + async GLB swap.
    super.createShip();
  }
  
  update(deltaTime, input) {
    super.update(deltaTime, input);

    const nowMs = this.getNowMs();

    // Charging builds automatically once initiated.
    if (this._chargeStartedAtMs) {
      const elapsed = Math.max(0, nowMs - this._chargeStartedAtMs);
      this.chargeTimer = Math.min(this.chargeMaxTime, elapsed / 1000);
      if (elapsed >= this.chargeMaxTime * 1000) {
        this._chargeStartedAtMs = 0;
        this.chargeTimer = this.chargeMaxTime;
        this._chargedUntilMs = nowMs + 2600;
        if (this.game.dialogueManager) this.game.dialogueManager.trigger('elephant', 'charge_max');
        this.game?.uiManager?.setCharacterExpression?.(this.character, "smug", { ttlMs: 1100 });
      }
    }

    // Base damage stays stable; charged shot is applied in getProjectileDamage().
    this.damage = 20;

    // Visual: charge core while charging/primed (lazy-loaded on first use).
    const active = !!this._chargeStartedAtMs || (this._chargedUntilMs && nowMs < this._chargedUntilMs);
    this.showActivatedShip(active);
    if (active) this.loadChargeCore();
    if (this._chargeCore) {
      this._chargeCore.visible = active;
      if (active) {
        const pulse = 0.85 + 0.15 * Math.sin((nowMs / 1000) * 6.2);
        this._chargeCore.scale.setScalar(pulse);
        this._chargeCore.rotation.y += deltaTime * 0.7;
      }
    }
  }

  activateSpecial(nowMs) {
    if (this._chargeStartedAtMs) return false;
    if (this._chargedUntilMs && nowMs < this._chargedUntilMs) return false;
    this._chargeStartedAtMs = nowMs;
    this.chargeTimer = 0;
    this.game?.uiManager?.setCharacterExpression?.(this.character, "angry", { ttlMs: 900 });
    this.loadChargeCore();
    this.game?.feedback?.shakeScreen?.(0.08, 0.14);
    return true;
  }

  getSpecialHudState(nowMs = this.getNowMs()) {
    const base = super.getSpecialHudState(nowMs);
    if (!base) return null;
    if (this._chargeStartedAtMs) {
      const elapsed = Math.max(0, nowMs - this._chargeStartedAtMs);
      const total = Math.max(1, this.chargeMaxTime * 1000);
      const pct = Math.max(0, Math.min(1, elapsed / total));
      return { ...base, isActive: true, isReady: false, progress: pct, mode: "charging" };
    }
    if (this._chargedUntilMs && nowMs < this._chargedUntilMs) {
      return { ...base, isActive: true, isReady: true, progress: 1, mode: "primed" };
    }
    return { ...base, mode: base.isReady ? "ready" : "cooldown" };
  }

  fire() {
    const before = this.lastFireTime;
    super.fire();
    const fired = this.lastFireTime !== before;
    if (!fired) return;
    const nowMs = this.getNowMs();
    if (this._chargedUntilMs && nowMs < this._chargedUntilMs) {
      this._chargedUntilMs = 0;
      this.game?.uiManager?.setCharacterExpression?.(this.character, "angry", { ttlMs: 550 });
    }
  }

  getProjectileDamage() {
    const nowMs = this.getNowMs();
    if (this._chargedUntilMs && nowMs < this._chargedUntilMs) return 80;
    return 20;
  }
  
  getQuote() {
    const quotes = [
      "Have we modeled second-order effects?",
      "Please don't.",
      "According to section 47-B, this is inadvisable.",
      "I've run the simulation 10,000 times. We die in 9,999.",
    ];
    return quotes[Math.floor(Math.random() * quotes.length)];
  }
}

/**
 * Mantis Shrimp - CHROMA WAR
 * Stealth, high damage, philosophical
 */
export class MantisShrimp extends Player {
  constructor(game, options = {}) {
    super(game, { ...options, character: 'mantisshrimp' });
    
    // Unique stats
    this.speed = 60;
    this.maxSpeed = 110;
    this.damage = 25;
    this.health = 70;
    this.shields = 70;
    this.maxHealth = this.health;
    this.maxShields = this.shields;

    // Personality Matrix
    this.personality = {
      aggression: 60,
      equity: 40,
      stability: 50,
      chaos: 70
    };
    
    // Ability
    this.isStealth = false;
    this.stealthTimer = 0;
    this.stealthMaxTime = 5;

    // Special ability: Phase Cloak (cooldown-gated stealth window).
    // Activated by `E` (preferred) or by braking while not holding forward.
    this.specialName = "SPECTRAL STRIKE";
    this.specialButtonHint = "E";
    this.specialCooldownMs = 14000;
    this.specialDurationMs = 6500;
    this.specialSummary = "Phases the ship for defense and a sharper counterattack.";
    this.specialUsage = "Press E when under pressure or when lining up a clean attack window.";
    this.specialEffect = "Incoming damage is heavily reduced and your shots hit harder while cloaked.";
    this.specialTacticalTip = "Use it to survive dense bullet patterns without losing offensive pressure.";
    this._cloakUntilMs = 0;
    this._cloakShell = null;
    this._cloakShellPromise = null;
  }

  loadCloakShell() {
    if (this._cloakShellPromise) return this._cloakShellPromise;
    const ensureFallback = () => {
      if (this._cloakShell) return this._cloakShell;
      const fallback = createFallbackCloakShellVisual({ color: 0xff66ff });
      fallback.name = "mantis-cloak-shell-fallback";
      fallback.visible = false;
      fallback.position.set(0, 4, 8);
      this.object3D.add(fallback);
      this._cloakShell = fallback;
      return fallback;
    };
    const url = getAbilityGlbUrl("chroma_phase_shell");
    if (!url) {
      return Promise.resolve(ensureFallback());
    }
    ensureFallback();
    this._cloakShellPromise = this.createGlbInstance(url, { targetMaxDim: 34, cloneMaterials: true }).then((inst) => {
      if (!inst) {
        this._cloakShellPromise = null;
        return ensureFallback();
      }
      inst.name = "mantis-cloak-shell";
      inst.userData.isAbilityGlb = true;
      inst.visible = false;
      inst.position.set(0, 4, 8);
      markAbilityObjectReadable(inst);
      inst.traverse((obj) => {
        if (!obj?.isMesh) return;
        const mat = obj.material;
        if (mat && typeof mat === "object") {
          if ("transparent" in mat) mat.transparent = true;
          if ("opacity" in mat) mat.opacity = Math.min(Number(mat.opacity) || 1, 0.22);
          if ("depthWrite" in mat) mat.depthWrite = false;
          if ("emissiveIntensity" in mat) mat.emissiveIntensity = Math.max(Number(mat.emissiveIntensity) || 0, 0.35);
          mat.needsUpdate = true;
        }
      });
      if (this._cloakShell && this._cloakShell !== inst) {
        try {
          this.object3D.remove(this._cloakShell);
        } catch {
          // ignore
        }
      }
      this.object3D.add(inst);
      this._cloakShell = inst;
      return inst;
    }).catch(() => {
      this._cloakShellPromise = null;
      return ensureFallback();
    });
    return this._cloakShellPromise;
  }

  preloadAbilityAssets() {
    return this.loadCloakShell();
  }
  
  async createShip() {
    // Use the base ship pipeline: instant placeholder + async GLB swap.
    super.createShip();
  }
  
  update(deltaTime, input) {
    super.update(deltaTime, input);

    const nowMs = this.getNowMs();
    if (input?.special) {
      this.tryActivateSpecial(nowMs);
    }

    const isCloaked = nowMs < this._cloakUntilMs;
    this.showActivatedShip(isCloaked);
    this.isStealth = isCloaked;
    this.stealthTimer = isCloaked ? Math.min(this.stealthMaxTime, this.stealthTimer + deltaTime) : 0;
    this.applyShipOpacity(isCloaked ? 0.25 : 0.8);

    if (isCloaked) this.loadCloakShell();
    if (this._cloakShell) {
      this._cloakShell.visible = isCloaked;
      if (isCloaked) this._cloakShell.rotation.y += deltaTime * 0.35;
    }
  }

  getProjectileDamage() {
    const base = super.getProjectileDamage();
    // “Stealth strike”: shots hit significantly harder while cloaked.
    return this.isStealth ? Math.round(base * 2) : base;
  }

  getIncomingDamage(amount) {
    if (amount <= 0) return amount;
    // While cloaked, incoming damage is heavily reduced.
    return this.isStealth ? amount * 0.4 : amount;
  }

  activateSpecial(nowMs) {
    this._cloakUntilMs = nowMs + Math.max(400, Number(this.specialDurationMs) || 4500);
    if (this.game.dialogueManager) this.game.dialogueManager.trigger('mantisshrimp', 'stealth_on');
    this.game?.uiManager?.setCharacterExpression?.(this.character, "smug", { ttlMs: 900 });
    this.loadCloakShell();
    this.game?.feedback?.shakeScreen?.(0.06, 0.12);
    return true;
  }
  
  getQuote() {
    const quotes = [
      "You are seeing only three dimensions of this problem.",
      "The black hole is merely a metaphor.",
      "Let me explain why you've already lost.",
      "This quarter's projections look challenging.",
    ];
    return quotes[Math.floor(Math.random() * quotes.length)];
  }
}

/**
 * Ant - FULFILLMENT QUEEN
 * Swarm deployment, logistics, efficiency
 */
export class Ant extends Player {
  constructor(game, options = {}) {
    super(game, { ...options, character: 'ant' });
    
    // Unique stats
    this.speed = 50;
    this.maxSpeed = 90;
    this.damage = 12;
    this.health = 100;
    this.shields = 100;
    this.maxHealth = this.health;
    this.maxShields = this.shields;
    this.fireRate = 0.1; // Faster fire rate for swarm

    // Personality Matrix
    this.personality = {
      aggression: 40,
      equity: 95,
      stability: 70,
      chaos: 20
    };
    
    // Swarm
    this.swarm = [];
    this.maxSwarm = 100;
    this.swarmRange = 150;
    this.powerUpPickupRadius = 18;
    this._swarmRegenAcc = 0;
    this._swarmFireAcc = 0;
    this._swarmFireIndex = 0;
    this._swarmVisuals = [];
    this._swarmVisualMax = 12;

    // Special ability: Logistics Surge (cooldown-gated burst of efficiency).
    this.specialName = "SWARM CHAIN";
    this.specialButtonHint = "E";
    this.specialCooldownMs = 15000;
    this.specialDurationMs = 6500;
    this.specialSummary = "Triggers a logistics surge: shields up, swarm online, assist fire active.";
    this.specialUsage = "Press E after taking damage or before a dense wave needs cleanup.";
    this.specialEffect = "Restores shields, adds swarm units, and burst-fires assist projectiles.";
    this.specialTacticalTip = "Save it for recovery moments or when a wave is about to overwhelm the lane.";
    this._surgeUntilMs = 0;
    this._swarmletTemplate = null;
    this._swarmletTemplatePromise = null;
  }

  loadSwarmletTemplate() {
    if (this._swarmletTemplatePromise) return this._swarmletTemplatePromise;
    const url = getAbilityGlbUrl("fulfillment_swarmlet");
    if (!url) return Promise.resolve(null);
    this._swarmletTemplatePromise = this.createGlbInstance(url, { targetMaxDim: 3.6 }).then((inst) => {
      if (!inst) {
        this._swarmletTemplatePromise = null;
        return null;
      }
      markAbilityObjectReadable(inst);
      inst.userData.isAbilityGlb = true;
      this._swarmletTemplate = inst;
      // Upgrade any existing procedural visuals.
      for (let i = 0; i < this._swarmVisuals.length; i++) {
        const s = this._swarmVisuals[i];
        if (!s?.mesh) continue;
        if (s.mesh?.userData?.isAbilityGlb) continue;
        try {
          this.object3D.remove(s.mesh);
        } catch {
          // ignore
        }
        const m = this._swarmletTemplate.clone(true);
        m.name = "ant-swarmlet";
        m.userData.isAbilityGlb = true;
        m.visible = false;
        markAbilityObjectReadable(m);
        this.object3D.add(m);
        s.mesh = m;
      }
      return inst;
    }).catch(() => {
      this._swarmletTemplatePromise = null;
      return null;
    });
    return this._swarmletTemplatePromise;
  }

  preloadAbilityAssets() {
    this.ensureSwarmVisuals?.();
    return this.loadSwarmletTemplate();
  }

  ensureSwarmVisuals() {
    if (this._swarmVisualsReady) return;
    this._swarmVisualsReady = true;
    for (let i = 0; i < this._swarmVisualMax; i++) {
      const m = (() => {
        if (this._swarmletTemplate) {
          const inst = this._swarmletTemplate.clone(true);
          inst.userData.isAbilityGlb = true;
          inst.visible = false;
          markAbilityObjectReadable(inst);
          return inst;
        }
        const pending = createFallbackDroneVisual({ color: 0xa8ff99, scale: 0.38 });
        pending.name = "ant-swarmlet-fallback";
        pending.visible = false;
        pending.userData.pendingAbilityGlb = true;
        return pending;
      })();
      m.name = "ant-swarmlet";
      m.castShadow = false;
      m.receiveShadow = false;
      m.visible = false;
      this._swarmVisuals.push({ mesh: m, angle: (i / this._swarmVisualMax) * Math.PI * 2 });
      this.object3D.add(m);
    }
  }
  
  async createShip() {
    // Use the base ship pipeline: instant placeholder + async GLB swap.
    super.createShip();
    // Load the authored swarmlet model in the background (never blocks play).
    this.loadSwarmletTemplate();
  }
  
  update(deltaTime, input) {
    super.update(deltaTime, input);

    this.ensureSwarmVisuals();
    const nowMs = this.getNowMs();
    const isSurge = nowMs < this._surgeUntilMs;
    this.showActivatedShip(isSurge);

    // Deterministic swarm regen: logistics never sleeps.
    this._swarmRegenAcc += Math.max(0, deltaTime);
    const regenInterval = isSurge ? 0.1 : 0.22;
    while (this.swarm.length < this.maxSwarm && this._swarmRegenAcc >= regenInterval) {
      this._swarmRegenAcc -= regenInterval;
      this.swarm.push({ active: true });
      if (this.swarm.length === this.maxSwarm && this.game.dialogueManager) {
        this.game.dialogueManager.trigger('ant', 'swarm_max');
      }
    }

    // Show a small visible subset of the swarm orbiting the carrier.
    const fullness = Math.max(this.swarm.length / this.maxSwarm, isSurge ? 0.72 : 0);
    const visibleCount = Math.min(this._swarmVisualMax, Math.ceil(fullness * this._swarmVisualMax));
    for (let i = 0; i < this._swarmVisuals.length; i++) {
      const s = this._swarmVisuals[i];
      if (!s?.mesh) continue;
      s.mesh.visible = i < visibleCount;
      if (!s.mesh.visible) continue;
      const speedMul = isSurge ? 1.65 : 1.0;
      s.angle += deltaTime * (1.1 + i * 0.06) * speedMul;
      const x = Math.cos(s.angle) * 18;
      const z = Math.sin(s.angle) * 12;
      const y = 1.6 + Math.sin(s.angle * 2.2) * 1.0;
      s.mesh.position.set(x, y, z);
      const pulse = isSurge ? (1.18 + 0.06 * Math.sin((nowMs / 1000) * 10 + i)) : 1.0;
      s.mesh.scale.setScalar(pulse);
    }

    // Swarm assist fire: small, frequent chip damage that scales with swarm fullness.
    this._swarmFireAcc += Math.max(0, deltaTime);
    const fireInterval = isSurge ? 0.24 : 0.48;
    if (this.swarm.length > 0 && this._swarmFireAcc >= fireInterval) {
      this._swarmFireAcc = 0;
      const enemies = this.game?.enemies || [];
      let nearest = null;
      let best = Infinity;
      for (const e of enemies) {
        if (!e?.object3D || e.isDestroyed) continue;
        const dist = e.object3D.position.distanceTo(this.object3D.position);
        if (dist < best) {
          best = dist;
          nearest = e;
        }
      }
      if (nearest && best <= this.swarmRange && this.game?.enemyManager?.createProjectile) {
        const dmgBase = 3 + (this.swarm.length / this.maxSwarm) * 6;
        const dmg = Math.max(2, Math.round(isSurge ? dmgBase * 1.35 : dmgBase));
        const origin = this.object3D.position.clone().add(new THREE.Vector3(0, 2, 8));
        const baseDir = new THREE.Vector3().subVectors(nearest.object3D.position, origin).normalize();
        const spread = isSurge ? [-0.065, -0.03, 0, 0.03, 0.065] : [-0.045, 0, 0.045];
        for (let i = 0; i < spread.length; i++) {
          const dir = baseDir.clone().add(new THREE.Vector3(spread[i], 0, 0)).normalize();
          const proj = this.game.enemyManager.createProjectile(origin.clone(), dir, 230, dmg, true);
          this.game.scene?.add?.(proj.object3D);
          if (Array.isArray(this.game.projectiles)) this.game.projectiles.push(proj);
        }
      }
    }
  }

  activateSpecial(nowMs) {
    this.loadSwarmletTemplate();
    // Instant value: refill shields a bit and top up some swarm.
    this._surgeUntilMs = nowMs + Math.max(500, Number(this.specialDurationMs) || 4200);

    const before = this.shields;
    this.shields = Math.min(this.maxShields, this.shields + 40);
    if (this.game?.uiManager?.updateShields) this.game.uiManager.updateShields(this.shields, this.maxShields);
    if (this.game?.feedback?.playShieldRecharge && this.shields > before) {
      this.game.feedback.playShieldRecharge(this.object3D.position?.clone?.() || new THREE.Vector3());
    }

    const add = 18;
    for (let i = 0; i < add && this.swarm.length < this.maxSwarm; i++) this.swarm.push({ active: true });
    this._swarmFireAcc = 999; // burst fire immediately
    this.game?.uiManager?.setCharacterExpression?.(this.character, "smug", { ttlMs: 900 });
    this.game?.feedback?.shakeScreen?.(0.1, 0.18);
    return true;
  }
  
  getQuote() {
    const quotes = [
      "Package delivered.",
      "I've listed nearby condos around it.",
      "Your defeat has been Prime-shipped.",
      "I already own the company that made your weapon.",
    ];
    return quotes[Math.floor(Math.random() * quotes.length)];
  }
}

// Factory function
export function createPlayer(game, character, options = {}) {
  const characters = {
    honeybadger: HoneyBadger,
    octopus: Octopus,
    elephant: Elephant,
    mantisshrimp: MantisShrimp,
    ant: Ant,
  };
  
  const CharacterClass = characters[character] || Player;
  return new CharacterClass(game, options);
}
